// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngSticky from "@werner/App/Components/XngSticky";
import { XngStickyComponentProps } from "@werner/App/Components/XngSticky/XngSticky";
import { DivWrapper } from "@werner/XDL/constants";
import { hideElementOnPrint, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";
import { css } from "styled-components";

interface CartPageTemplateProps {
    children: React.ReactNode;
    extendedStyles?: CartPageTemplateStyles;
    isReview?: boolean;
}

interface Props {
    MainSection: React.FC<any>;
    HeaderSection: React.FC<any>;
    CartApproverMessage: React.FC<any>;
    CartFulfillmentSelector: React.FC<any>;
    CartTotalSection: React.FC<any>;
    ActionsSection: React.FC<any>;
}

interface CartPageTemplateContextProps {
    styles: CartPageTemplateStyles;
    isReview?: boolean;
}

export interface CartPageTemplateStyles {
    container?: InjectableCss;
    headerSection?: InjectableCss;
    mainSection?: InjectableCss;
    cartApproverMessage?: InjectableCss;
    cartFulfullmentSelector?: InjectableCss;
    totalSection?: InjectableCss;
    actionsSection?: InjectableCss;

    /* XNGAGE Changes */
    xngStickyStyles?: XngStickyComponentProps;
}

const CartPageTemplateContext = React.createContext<CartPageTemplateContextProps>({
    styles: {},
    isReview: false,
});

export const useCartPageTemplateContext = () => {
    const context = React.useContext(CartPageTemplateContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};

export const cartPageTemplateStyles: CartPageTemplateStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
            @media print {
                display: block;
            }
        `,
    },
    headerSection: {
        css: css`
            width: 100%;
            @media print {
                order: 0;
                h1 {
                    font-size: 40px !important;
                    line-height: 46px !important;
                    margin-bottom: 45px !important;
                    margin-top: 15px !important;
                }
            }
        `,
    },
    actionsSection: {
        css: css`
            width: 100%;
            ${hideElementOnPrint}
        `,
    },
    mainSection: {
        css: css`
            width: calc(100% - 284px);
            padding-right: 40px;
            ${xScreen("lte", "sm")} {
                width: 100%;
                padding: 0;
                margin-bottom: 0;
            }

            /* Start XNGAGE Changes */
            @media print {
                width: 100%;
                order: 2;
                margin-bottom: 48px;
            }
            /* END XNGAGE Changes */
        `,
    },
    totalSection: {
        css: css`
            width: 284px;

            // XNG-Sticky
            & > div.xng-sticky > ${DivWrapper} > div:not(:last-child) {
                margin-bottom: 20px;
            }

            ${xScreen("lte", "sm")} {
                width: 100%;
                display: flex;
                justify-content: center;
                order: 4;
                margin-top: 48px;
                & > div.xng-sticky {
                    max-width: 100%;
                    width: 100%;
                }
            }

            ${xScreen("lte", "xs")} {
                & > div.xng-sticky {
                    width: 100%;
                }
            }

            @media print {
                &&&& {
                    display: block;
                    width: 100%;
                    order: 1 !important;
                    margin-top: 0;
                    & > div.xng-sticky {
                        position: relative;
                        top: 0;
                        max-width: 100%;
                        width: 100%;
                    }
                }
            }
        `,
    },
    cartApproverMessage: {
        css: css`
            margin-bottom: 80px;
            &:has(div[class^="Wrapper-sc"]:empty) {
                display: none;
            }
            ${hideElementOnPrint}
        `,
    },
    cartFulfullmentSelector: {
        css: css`
            margin-bottom: 30px;
            &:has(div[class^="Wrapper-sc"]:empty) {
                display: none;
            }
            ${hideElementOnPrint}
        `,
    },
    /* Start XNGAGE Changes */
    xngStickyStyles: {
        css: css`
            top: 138px;

            ${xScreen("lte", "md")} {
                top: 168px;
            }

            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    /* END XNGAGE Changes */
};

const CartPageTemplate: FC<CartPageTemplateProps> & Props = ({ children, extendedStyles, isReview }) => {
    const [styles] = useState(() => mergeWithCss(cartPageTemplateStyles, extendedStyles));
    const value = useMemo(() => ({ styles, isReview }), [styles, isReview]);
    return (
        <CartPageTemplateContext.Provider value={value}>
            <StyledWrapper {...styles.container}>{children}</StyledWrapper>
        </CartPageTemplateContext.Provider>
    );
};

const HeaderSection: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.headerSection}>{children}</StyledWrapper>;
};

const CartApproverMessage: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartApproverMessage}>{children}</StyledWrapper>;
};

const CartFulfillmentSelector: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartFulfullmentSelector}>{children}</StyledWrapper>;
};

const MainSection: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.mainSection}>{children}</StyledWrapper>;
};

const CartTotalSection: FC<PropsWithChildren> = ({ children }) => {
    const { styles, isReview } = useCartPageTemplateContext();

    return (
        <StyledWrapper {...styles.totalSection} {...{ isReview }}>
            <XngSticky {...styles.xngStickyStyles}>{children}</XngSticky>
        </StyledWrapper>
    );
};

const ActionsSection: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.actionsSection}>{children}</StyledWrapper>;
};

CartPageTemplate.HeaderSection = HeaderSection;
CartPageTemplate.MainSection = MainSection;
CartPageTemplate.CartApproverMessage = CartApproverMessage;
CartPageTemplate.CartFulfillmentSelector = CartFulfillmentSelector;
CartPageTemplate.CartTotalSection = CartTotalSection;
CartPageTemplate.ActionsSection = ActionsSection;

export default CartPageTemplate;
